
import React, { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DashboardWidgets from "@/components/dashboard/DashboardWidgets";
import TripsTabContent from "@/components/dashboard/TripsTabContent";
import TripClaimHandler from "@/components/dashboard/TripClaimHandler";
import { useTrips } from "@/hooks/useTrips";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileTripTypeSelector from "@/components/dashboard/MobileTripTypeSelector";
import MobileModuleSelector from "@/components/dashboard/MobileModuleSelector";
import { Dialog } from "@/components/ui/dialog";
import ScheduleDialog from "@/components/dashboard/ScheduleDialog";
import EarningsDialog from "@/components/dashboard/EarningsDialog";
import RatingDetailDialog from "@/components/dashboard/RatingDetailDialog";
import { useQuery } from "@tanstack/react-query";
import { fetchRatings } from "@/services/ratingService";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("new");
  const [activeModule, setActiveModule] = useState("none");
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [earningsDialogOpen, setEarningsDialogOpen] = useState(false);
  const [ratingsDialogOpen, setRatingsDialogOpen] = useState(false);
  
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const { refetch: refetchNew } = useTrips({ tripType: "booked" });
  const { refetch: refetchUpcoming } = useTrips({ tripType: "claimed", enabled: activeTab === "upcoming" });
  const { refetch: refetchCompleted } = useTrips({ tripType: "complete", enabled: activeTab === "completed" });
  
  // Fetch ratings data for the dialog
  const { data: ratingsData } = useQuery({
    queryKey: ["ratings"],
    queryFn: fetchRatings,
    enabled: ratingsDialogOpen, // Only fetch when dialog is open
  });
  
  // Calculate the ratings distribution
  const ratingDistribution = React.useMemo(() => {
    if (!ratingsData?.summary) return [];
    
    const totalReviews = ratingsData.count || 0;
    if (totalReviews === 0) return [];
    
    return [
      { stars: 5, percentage: (ratingsData.summary["5-star"] / totalReviews) * 100 },
      { stars: 4, percentage: (ratingsData.summary["4-star"] / totalReviews) * 100 },
      { stars: 3, percentage: (ratingsData.summary["3-star"] / totalReviews) * 100 },
      { stars: 2, percentage: (ratingsData.summary["2-star"] / totalReviews) * 100 },
      { stars: 1, percentage: (ratingsData.summary["1-star"] / totalReviews) * 100 },
    ];
  }, [ratingsData]);
  
  useEffect(() => {
    console.log(`Current tab: ${activeTab}`);
  }, [activeTab]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };
  
  const handleModuleSelect = (module: string) => {
    setActiveModule(module);
    
    // Close any open dialogs first
    setScheduleDialogOpen(false);
    setEarningsDialogOpen(false);
    setRatingsDialogOpen(false);
    
    // Open the selected module dialog
    switch (module) {
      case "schedule":
        setScheduleDialogOpen(true);
        break;
      case "earnings":
        setEarningsDialogOpen(true);
        break;
      case "ratings":
        setRatingsDialogOpen(true);
        break;
      default:
        break;
    }
  };
  
  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 pb-10">
        <TripClaimHandler
          onSuccess={() => {
            refetchNew();
            refetchUpcoming();
          }}
        >
          {(handleClaimTrip) => (
            <>
              {isMobile ? (
                <div className="w-full mb-6">
                  {/* Mobile selectors in a flex row */}
                  <div className="flex gap-2 mb-4">
                    <div className="w-1/2">
                      {/* Added label above Trip Type selector */}
                      <div className="mb-2 text-black font-medium font-newgrotesk">View Trips</div>
                      <MobileTripTypeSelector 
                        activeTab={activeTab} 
                        onTabChange={handleTabChange}
                      />
                    </div>
                    <div className="w-1/2">
                      {/* Added label above Module selector */}
                      <div className="mb-2 text-black font-medium font-newgrotesk">View Analytics</div>
                      <MobileModuleSelector 
                        onModuleSelect={handleModuleSelect}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    {activeTab === "new" && (
                      <TripsTabContent
                        type="booked"
                        activeTab={activeTab}
                        onClaimTrip={handleClaimTrip}
                      />
                    )}
                    
                    {activeTab === "upcoming" && (
                      <TripsTabContent
                        type="claimed"
                        activeTab={activeTab}
                      />
                    )}
                    
                    {activeTab === "completed" && (
                      <TripsTabContent
                        type="complete"
                        activeTab={activeTab}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <Tabs 
                  defaultValue="new" 
                  className="w-full"
                  value={activeTab}
                  onValueChange={handleTabChange}
                >
                  <div className="mb-6">
                    <TabsList className="bg-transparent p-0 w-full justify-start border-b">
                      <TabsTrigger 
                        value="new" 
                        className="data-[state=active]:border-b-2 data-[state=active]:border-black rounded-none px-6 py-3 text-lg font-medium"
                      >
                        New
                      </TabsTrigger>
                      <TabsTrigger 
                        value="upcoming" 
                        className="data-[state=active]:border-b-2 data-[state=active]:border-black rounded-none px-6 py-3 text-lg font-medium"
                      >
                        Upcoming
                      </TabsTrigger>
                      <TabsTrigger 
                        value="completed" 
                        className="data-[state=active]:border-b-2 data-[state=active]:border-black rounded-none px-6 py-3 text-lg font-medium"
                      >
                        Completed
                      </TabsTrigger>
                    </TabsList>
                    <p className="text-sm text-gray-500 mt-2">View and manage your trips</p>
                  </div>
                  
                  <TabsContent value="new" className="mt-0">
                    <TripsTabContent
                      type="booked"
                      activeTab={activeTab}
                      onClaimTrip={handleClaimTrip}
                    />
                  </TabsContent>
                  
                  <TabsContent value="upcoming" className="mt-0">
                    <TripsTabContent
                      type="claimed"
                      activeTab={activeTab}
                    />
                  </TabsContent>
                  
                  <TabsContent value="completed" className="mt-0">
                    <TripsTabContent
                      type="complete"
                      activeTab={activeTab}
                    />
                  </TabsContent>
                </Tabs>
              )}
            </>
          )}
        </TripClaimHandler>
        
        {!isMobile && <DashboardWidgets />}
      </div>
      
      {/* Module Dialogs */}
      <ScheduleDialog 
        open={scheduleDialogOpen} 
        onOpenChange={setScheduleDialogOpen} 
      />
      
      <EarningsDialog 
        open={earningsDialogOpen} 
        onOpenChange={setEarningsDialogOpen} 
      />
      
      <RatingDetailDialog
        open={ratingsDialogOpen}
        onOpenChange={setRatingsDialogOpen}
        ratings={ratingsData?.results || []}
        averageRating={ratingsData?.summary?.average_rating || 0}
        totalReviews={ratingsData?.count || 0}
        distribution={ratingDistribution}
      />
    </DashboardLayout>
  );
};

export default Dashboard;
